import React, { useState } from 'react';
import { Box, Typography, Link as MuiLink, CardMedia, Button, Snackbar, Alert, IconButton } from '@mui/material';
import { useParams } from 'react-router-dom';
import Navbar from '../components/Navbar';
import HeroBanner from '../components/HeroBanner';
import Footer from '../components/Footer';
import Breadcrumb from '../components/Breadcrumb';
import ReactMarkdown from 'react-markdown';
import CloseIcon from '@mui/icons-material/Close';
import { modelItems } from '../data/SiteData';
import { useNavigate } from 'react-router-dom';

const SimulationPage = () => {
  const { modelId } = useParams();
  const navigate = useNavigate();
  const model = modelItems.find((item) => item.modelId === modelId);

  const [snackbarOpen, setSnackbarOpen] = useState(false);

  if (!model) return <Typography>Model not found.</Typography>;

  const breadcrumbItems = [
    { title: 'Home', path: '/' },
    { title: 'Models', path: '/models' },
    {
      title: model.title.length > 30 ? model.title.substring(0, 30) + '...' : model.title,
      path: model.link,
    },
    { title: 'Simulation', path: model.simLink },
  ];

  const handleSnackbarOpen = () => setSnackbarOpen(true);
  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') return;
    setSnackbarOpen(false);
  };

  return (
    <>
      <Navbar />
      <HeroBanner
        heroTitle="Models"
        heroDescription="Find models and research data.."
        imageUrl="/images/digiTwins.png"
        height="165px"
      />
      <Breadcrumb items={breadcrumbItems} />

      <Box sx={{ padding: '32px', maxWidth: '1300px', margin: '0 auto' }}>
        {/* Block One */}
        <Box sx={{ display: 'flex', border: '0.5px solid #489596', padding: '16px', marginBottom: '32px' }}>
          {/* Left Column: Image */}
          <Box sx={{ flexShrink: 0, marginRight: '24px' }}>
            {model.image && (
              <CardMedia
                component="img"
                image={model.image}
                alt={model.title}
                sx={{ width: '120px' }}
              />
            )}
          </Box>

          {/* Right Column: Title + Metadata */}
          <Box sx={{ flex: 1 }}>
            {/* Title */}
            <Typography sx={{ fontFamily: 'Poppins', fontSize: '24px', fontWeight: 400, color: '#142434', marginBottom: '16px' }}>
              {model.title}
            </Typography>

            {/* Metadata in two columns */}
            <Box sx={{ display: 'flex', gap: '48px', marginBottom: '16px' }}>
              {/* Column 1 */}
            <Box sx={{ flex: 1 }}>
                {model.foci && (
                  <>
                    <Typography sx={{ fontFamily: 'PT Sans', fontWeight: 'bold', fontSize: '16px', color: '#142434', textTransform: 'uppercase' }}>
                      Research Focus
                    </Typography>
                    {model.foci.map((f, i) => (
                      <Typography key={i} sx={{ fontFamily: 'PT Sans', fontSize: '16px', color: '#142434', marginBottom: '8px' }}>
                        {f.focus}
                      </Typography>
                    ))}
                  </>
                )}
                {model.conditions && (
                  <>
                    <Typography sx={{ fontFamily: 'PT Sans', fontWeight: 'bold', fontSize: '16px', color: '#142434', textTransform: 'uppercase' }}>
                      Condition
                    </Typography>
                    {model.conditions.map((c, i) => (
                      <Typography key={i} sx={{ fontFamily: 'PT Sans', fontSize: '16px', color: '#142434', marginBottom: '8px' }}>
                        {c.condition}
                      </Typography>
                    ))}
                  </>
                )}
              </Box>

              {/* Column 2 - Personnel further right */}
            <Box sx={{ flex: 1, marginLeft: '16px' }}>
                <Typography sx={{ fontFamily: 'PT Sans', fontWeight: 'bold', fontSize: '16px', color: '#142434', textTransform: 'uppercase' }}>
                  Personnel
                </Typography>
                {model.authors?.map((author, i) => (
                  <Typography key={i} sx={{ fontFamily: 'PT Sans', fontSize: '16px', color: '#142434', marginBottom: '8px' }}>
                    {author.orcid ? (
                      <MuiLink
                        href={`https://orcid.org/${author.orcid}`}
                        target="_blank"
                        rel="noopener"
                        sx={{
                          fontFamily: 'PT Sans',
                          color: '#489596',
                          textDecoration: 'none',
                          '&:hover': { textDecoration: 'underline' },
                        }}
                      >
                        {author.name}
                      </MuiLink>
                    ) : (
                      author.name
                    )}
                  </Typography>
                ))}
              </Box>
            </Box>

            {/* Secondary Button */}
            <Button
  onClick={() => navigate(model.link)} // internal navigation
  sx={{
    width: "240px",
    height: "42px",
    backgroundColor: "#FFFFFF",
    border: "2px solid #489596",
    fontFamily: "Poppins, sans-serif",
    fontSize: "16px",
    fontWeight: 600,
    color: "#489596",
    textTransform: "none",
    boxShadow: "none",
    "&:hover": { backgroundColor: "#ecf1f1" },
  }}
>
  Go to Model Page
</Button>

          </Box>
        </Box>

         {/* Bottom section */}
      <Box sx={{ maxWidth: '1300px', margin: '32px auto', paddingTop: '32px' }}>
        <CardMedia
          component="img"
          image={model.simImage}
          alt={`${model.title} simulation`}
          sx={{ cursor: 'pointer', maxWidth: '100%' }}
          onClick={handleSnackbarOpen}
        />
      </Box>

        {/* Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={5000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{ top: '16px', width: 'auto', padding: '0 16px', zIndex: 1300 }}
      >
        <Alert
          severity="info"
          onClose={handleSnackbarClose}
          action={
            <IconButton
              aria-label="close"
              color="inherit"
              size="small"
              onClick={handleSnackbarClose}
            >
              <CloseIcon fontSize="inherit" />
            </IconButton>
          }
          sx={{
            width: '100%',
            border: "1px solid",
            borderColor: "info.main",
            p: 2,
          }}
        >
          A model simulation engine has not been implemented as part of the demonstration prototype
        </Alert>
      </Snackbar>
      </Box>

      <Footer />
    </>
  );
};

export default SimulationPage;
